import {inject, Injectable} from '@angular/core';
import {ToastController, ToastOptions} from '@ionic/angular/standalone';
import {logger} from './logger.service';

/**
 * Service de gestion des notifications toast dans l'application.
 * 
 * Ce service fournit une interface simplifiée pour afficher des notifications
 * à l'utilisateur via des toasts Ionic.
 * 
 * @example
 * ```typescript
 * // Afficher une erreur
 * this.toastService.showToastError('Une erreur est survenue');
 * 
 * // Afficher un succès
 * this.toastService.showToastSuccess('Opération réussie');
 * 
 * // Afficher un toast personnalisé
 * this.toastService.showToast('Message personnalisé', 'warning');
 * ```
 */
@Injectable({
	providedIn: 'root'
})
export class ToastService {
	private readonly toastController: ToastController = inject(ToastController);

	/**
	 * Affiche un toast d'erreur (rouge).
	 * 
	 * @param message - Le message d'erreur à afficher
	 */
	showToastError(message: string): void {
		this.showToast(message, 'danger');
	}

	/**
	 * Affiche un toast de succès (vert).
	 * 
	 * @param message - Le message de succès à afficher
	 */
	showToastSuccess(message: string): void {
		this.showToast(message, 'success');
	}

	/**
	 * Affiche un toast avec un message et une couleur personnalisés.
	 * 
	 * @param message - Le message à afficher
	 * @param color - La couleur du toast ('danger', 'success', 'warning', 'primary', etc.)
	 */
	showToast(message: string, color: string): void {
		this.toastController
			.create({
				message,
				duration: 10000,
				cssClass: `yourClassName ${color}Toast`
			})
			.then((toast) => toast.present())
			.catch((error) => {
				// Log l'erreur silencieusement si le toast ne peut pas être affiché
				logger.error('[ToastService] Erreur lors de l\'affichage du toast:', error);
			});
	}

	/**
	 * Crée un toast personnalisé avec des options complètes.
	 * 
	 * @param options - Options de configuration du toast
	 * @returns Promise résolue avec l'instance du toast créé
	 */
	create(options: ToastOptions): Promise<HTMLIonToastElement> {
		return this.toastController.create(options);
	}
}
